/**
 * Reserved product colours (2026-09-23, Petal_Some Ug: "In fragrances we have
 * some that are transparent/clear in color so it would be good if we included
 * it as a color as well").
 *
 * A product colour is stored as a 6-digit hex in every copy the product keeps
 * (colours, variants, barcodes), and several screens append a 2-digit alpha to
 * it (`${hex}15` for a selected chip's tint). So "Transparent" cannot be the
 * CSS keyword or an 8-digit hex: it is a RESERVED near-white 6-digit hex, and
 * the swatches (components/ui/ColorSwatch.tsx, the Product Studio colour
 * picker) draw a checkerboard when they see it. Anything that does not know
 * about it simply shows a near-white dot, which still reads as "clear".
 *
 * The hex is the one signal. The name is what the shop filter matches on
 * (case-insensitive, components/shop/product-constants.ts already offers
 * "Transparent"), so the preset must keep exactly this name.
 */

export const TRANSPARENT_COLOR_NAME = "Transparent";

/** Reserved: never use this hex for any other colour. */
export const TRANSPARENT_COLOR_HEX = "#FDFEFF";

/** The Product Studio preset ("More" colours). */
export const TRANSPARENT_COLOR = {
  name: TRANSPARENT_COLOR_NAME,
  hex: TRANSPARENT_COLOR_HEX,
} as const;

/** True for the reserved Transparent hex (any case, with or without the "#"). */
export function isTransparentHex(hex: string | null | undefined): boolean {
  if (!hex) return false;
  const h = String(hex).trim().toUpperCase();
  return (h.startsWith("#") ? h : `#${h}`) === TRANSPARENT_COLOR_HEX;
}

/**
 * Inline style for a checkerboard swatch — the usual "this is see-through"
 * picture. Longhand properties only, so it can be merged into a style object
 * that already sets a border without React's shorthand/longhand warning.
 * `cellPx` is the size of one square.
 */
export function checkerboardStyle(cellPx = 4): {
  backgroundColor: string;
  backgroundImage: string;
  backgroundSize: string;
} {
  const tile = Math.max(2, Math.round(cellPx)) * 2;
  return {
    backgroundColor: "#ffffff",
    backgroundImage: "repeating-conic-gradient(#d4d4d8 0% 25%, #ffffff 0% 50%)",
    backgroundSize: `${tile}px ${tile}px`,
  };
}
